<template>
  <LeadPopoverMenu :items="menuItems">
    <template #trigger="{ toggleDropdown }">
      <LeadButton
        variant="ghost"
        data-testid="order-actions-menu"
        size="sm"
        :icon="IconDotsVertical"
        :aria-label="$t('btn.more')"
        @click="toggleDropdown" />
    </template>
  </LeadPopoverMenu>
</template>

<script setup lang="ts">
import { computed, inject } from 'vue';
import { IconDotsVertical, IconEye, IconLink, IconTrash } from '@tabler/icons-vue';
import type { Order } from '@/types/types';
import { useCustomerFeaturesStore } from '@/stores/customerFeatures';
import type { Dispatcher } from '@/events/dispatcher';
import { createPreviewModalOpenEvent } from '@/events/modalEvents';
import LeadButton from '@/components/lead-design/LeadButton.vue';
import LeadPopoverMenu, { type LeadPopoverMenuItem } from '@/components/lead-design/LeadPopoverMenu.vue';
import translator from '@/locale/translator';

const props = defineProps<{ order: Order }>();
const emit = defineEmits<{ (e: 'delete', order: Order): void }>();

const featureStore = useCustomerFeaturesStore();
const dispatcher = inject<Dispatcher>('dispatcher');

const allowPreviews = computed(() => Boolean(featureStore.featureOrderManagment?.allow_previews));

function openPreviewModal() {
  if (!props.order.id) return;
  dispatcher?.dispatch(createPreviewModalOpenEvent(props.order.id));
}

const menuItems = computed<LeadPopoverMenuItem[]>(() => {
  const items: LeadPopoverMenuItem[] = [];
  if (props.order.directlink) {
    items.push({
      key: 'directlink',
      label: translator.get('orders.details.open_directlink.tooltip') as string,
      icon: IconLink,
      href: props.order.directlink,
      target: '_blank',
    });
  }
  if (allowPreviews.value) {
    items.push({
      key: 'preview',
      label: translator.get('orders.preview_modal.tooltip') as string,
      icon: IconEye,
      onClick: openPreviewModal,
    });
  }
  if (props.order.is_draft) {
    items.push({
      key: 'delete',
      label: translator.get('btn.delete') as string,
      icon: IconTrash,
      class: 'tw:text-red-600! tw:hover:text-red-600!',
      onClick: () => emit('delete', props.order),
    });
  }
  return items;
});
</script>
